export interface SuperHitCandidate {
  status?: 'valid' | 'invalid' | string;
  services?: string[];
  verificationResult?: Record<string, unknown> | null;
  credentials?: Record<string, unknown> | null;
}

/** Services IA reconnus (clef API) */
export const AI_API_SERVICES = [
  'OpenAI',
  'Anthropic',
  'Google AI',
  'HuggingFace',
  'Groq',
  'Cohere',
  'Mistral',
] as const;

export type AiApiService = (typeof AI_API_SERVICES)[number];

const AI_SERVICE_ALIASES: Record<string, AiApiService> = {
  openai: 'OpenAI',
  'open ai': 'OpenAI',
  'open-ai': 'OpenAI',
  gpt: 'OpenAI',
  anthropic: 'Anthropic',
  claude: 'Anthropic',
  'google ai': 'Google AI',
  googleai: 'Google AI',
  'google-ai': 'Google AI',
  gemini: 'Google AI',
  huggingface: 'HuggingFace',
  'hugging face': 'HuggingFace',
  'hugging-face': 'HuggingFace',
  hf: 'HuggingFace',
  groq: 'Groq',
  cohere: 'Cohere',
  mistral: 'Mistral',
};

const normalizeServiceName = (service: string): string =>
  service
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

export const resolveAiApiService = (service: string): AiApiService | null => {
  const n = normalizeServiceName(service);
  if (AI_SERVICE_ALIASES[n]) return AI_SERVICE_ALIASES[n];
  const exact = AI_API_SERVICES.find((s) => normalizeServiceName(s) === n);
  return exact ?? null;
};

export const getAiApiServices = (hit: SuperHitCandidate): AiApiService[] => {
  const found = new Set<AiApiService>();
  (hit.services ?? []).forEach((service) => {
    const resolved = resolveAiApiService(service);
    if (resolved) found.add(resolved);
  });

  // Credentials keys (openai, anthropic, …)
  if (hit.credentials && typeof hit.credentials === 'object') {
    Object.keys(hit.credentials).forEach((key) => {
      const resolved = resolveAiApiService(key);
      if (resolved) found.add(resolved);
    });
  }

  return Array.from(found);
};

export const isAiApiHit = (hit: SuperHitCandidate): boolean => getAiApiServices(hit).length > 0;

export const isOpenAiOrAnthropicHit = (hit: SuperHitCandidate): boolean => {
  const services = getAiApiServices(hit);
  return services.includes('OpenAI') || services.includes('Anthropic');
};

/** Modèles premium OpenAI / Anthropic → tag WAO */
export interface EliteAiModelMatch {
  id: string;
  label: string;
}

const ELITE_MODEL_PATTERNS: Array<{ id: string; label: string; pattern: RegExp }> = [
  { id: 'gpt-6-astra', label: 'GPT-6 Astra', pattern: /gpt[\s_\-]?6[\s_\-]?(astra|atsra)|astra[\s_\-]?6|atsra[\s_\-]?6/i },
  { id: 'claude-opus-5', label: 'Claude Opus 5', pattern: /claude[\s_\-]?opus[\s_\-]?5|opus[\s_\-]?5(?!\.)/i },
  { id: 'claude-opus-4.1', label: 'Claude Opus 4.1', pattern: /claude[\s_\-]?opus[\s_\-]?4[\s_\-\.]?1|opus[\s_\-]?4[\s_\-\.]?1/i },
  { id: 'gpt-4.1', label: 'GPT-4.1', pattern: /gpt[\s_\-]?4[\s_\-\.]?1\b/i },
];

const collectSearchableStrings = (value: unknown, depth = 0, out: string[] = []): string[] => {
  if (depth > 6 || value == null) return out;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    out.push(String(value));
    return out;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectSearchableStrings(item, depth + 1, out));
    return out;
  }
  if (typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([k, v]) => {
      out.push(k);
      collectSearchableStrings(v, depth + 1, out);
    });
  }
  return out;
};

const pickAiPayload = (hit: SuperHitCandidate): unknown => {
  const vr = hit.verificationResult ?? {};
  const creds = hit.credentials ?? {};
  const keys = [
    'openai',
    'OpenAI',
    'anthropic',
    'Anthropic',
    'models',
    'Models',
    'model',
    'Model',
    'availableModels',
    'AvailableModels',
    'ai',
    'AI',
  ];

  const bag: unknown[] = [vr, creds];
  keys.forEach((key) => {
    if ((vr as Record<string, unknown>)[key] != null) bag.push((vr as Record<string, unknown>)[key]);
    if ((creds as Record<string, unknown>)[key] != null) bag.push((creds as Record<string, unknown>)[key]);
  });
  return bag;
};

export const getEliteAiModels = (hit: SuperHitCandidate): EliteAiModelMatch[] => {
  if (!isOpenAiOrAnthropicHit(hit) || hit.status !== 'valid') return [];

  const haystack = collectSearchableStrings(pickAiPayload(hit)).join(' | ');
  const matches: EliteAiModelMatch[] = [];

  for (const entry of ELITE_MODEL_PATTERNS) {
    if (entry.pattern.test(haystack)) {
      matches.push({ id: entry.id, label: entry.label });
    }
  }

  return matches;
};

export const hasEliteAiModel = (hit: SuperHitCandidate): boolean => getEliteAiModels(hit).length > 0;

const BEDROCK_TRUE_KEYS = [
  'bedrock',
  'Bedrock',
  'hasBedrock',
  'HasBedrock',
  'canUseBedrock',
  'CanUseBedrock',
  'bedrockAccess',
  'BedrockAccess',
  'CanInvokeBedrock',
  'canInvokeBedrock',
  'InvokeModel',
  'bedrockEnabled',
];

const looksLikeBedrockTrue = (value: unknown): boolean => {
  if (value === true) return true;
  if (typeof value === 'number' && value > 0) return true;
  if (typeof value === 'string') {
    const n = value.trim().toLowerCase();
    return n === 'true' || n === 'yes' || n === 'allowed' || n === 'enabled' || n === 'ok';
  }
  return false;
};

export const hasBedrockAccess = (hit: SuperHitCandidate): boolean => {
  if (hit.status !== 'valid') return false;
  const services = hit.services ?? [];
  const isAws =
    services.some((s) => normalizeServiceName(s) === 'aws') ||
    !!(hit.credentials && (hit.credentials as Record<string, unknown>).aws);

  if (!isAws) return false;

  // Service tag explicite
  if (services.some((s) => /bedrock/i.test(s))) return true;

  const vr = hit.verificationResult ?? {};
  const creds = hit.credentials ?? {};
  const awsVr = (vr as Record<string, unknown>).aws ?? (vr as Record<string, unknown>).AWS;
  const iam = (vr as Record<string, unknown>).iam ?? (awsVr as Record<string, unknown> | undefined)?.iam;
  const bedrockNode =
    (vr as Record<string, unknown>).bedrock ??
    (vr as Record<string, unknown>).Bedrock ??
    (awsVr as Record<string, unknown> | undefined)?.bedrock ??
    (awsVr as Record<string, unknown> | undefined)?.Bedrock;

  if (looksLikeBedrockTrue(bedrockNode)) return true;
  if (bedrockNode && typeof bedrockNode === 'object') {
    const node = bedrockNode as Record<string, unknown>;
    if (
      looksLikeBedrockTrue(node.IsValid) ||
      looksLikeBedrockTrue(node.isValid) ||
      looksLikeBedrockTrue(node.allowed) ||
      looksLikeBedrockTrue(node.enabled) ||
      looksLikeBedrockTrue(node.access) ||
      looksLikeBedrockTrue(node.CanInvoke) ||
      looksLikeBedrockTrue(node.canInvoke) ||
      (typeof node.ModelCount === 'number' && node.ModelCount > 0)
    ) {
      return true;
    }
  }

  for (const key of BEDROCK_TRUE_KEYS) {
    if (looksLikeBedrockTrue((vr as Record<string, unknown>)[key])) return true;
    if (looksLikeBedrockTrue((creds as Record<string, unknown>)[key])) return true;
    if (iam && looksLikeBedrockTrue((iam as Record<string, unknown>)[key])) return true;
  }

  // Scan textuel des politiques / permissions
  const haystack = collectSearchableStrings([vr, creds, iam, bedrockNode]).join(' ');
  if (/bedrock:(invoke|invoke-model|\*)/i.test(haystack)) return true;
  if (/amazon\.bedrock|aws\.bedrock|bedrockruntime/i.test(haystack)) return true;
  if (/\bBedrock\b/.test(haystack) && /(allow|allowed|enabled|access|permission|invoke)/i.test(haystack)) {
    return true;
  }

  return false;
};

export const getTwilioBalanceValue = (hit: SuperHitCandidate): number | null => {
  const raw =
    (hit.credentials?.twilio as { balance?: unknown } | undefined)?.balance ??
    hit.verificationResult?.TwilioBalance ??
    (hit.services?.includes('Twilio') ? hit.verificationResult?.Balance : undefined);

  if (raw == null || raw === '') return null;

  const normalized = String(raw).trim().replace(/\s/g, '').replace(',', '.');
  const match = normalized.match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;

  const parsed = parseFloat(match[0]);
  return Number.isNaN(parsed) ? null : parsed;
};

export const formatTwilioBalance = (hit: SuperHitCandidate): string | null => {
  const raw =
    (hit.credentials?.twilio as { balance?: unknown } | undefined)?.balance ??
    hit.verificationResult?.TwilioBalance;

  if (raw != null && String(raw).trim() !== '') {
    const str = String(raw).trim();
    if (str.includes('€') || str.includes('$')) return str;
    const value = getTwilioBalanceValue(hit);
    return value != null ? `${value}€` : str;
  }

  const value = getTwilioBalanceValue(hit);
  return value != null ? `${value}€` : null;
};

export const isSuperHit = (hit: SuperHitCandidate): boolean => {
  if (hit.status !== 'valid') return false;

  // Clef API IA valide = Super Hit
  if (isAiApiHit(hit)) return true;

  // AWS Bedrock = Super Hit
  if (hasBedrockAccess(hit)) return true;

  const vr = hit.verificationResult;
  if (!vr) {
    return hit.services?.includes('SMTP') ?? false;
  }

  const ses = vr.ses as { Quota?: number } | undefined;
  if (ses?.Quota !== undefined && ses.Quota > 200) return true;

  const sns = vr.sns as { Balance?: string } | undefined;
  if (sns?.Balance && parseFloat(sns.Balance) > 1) return true;

  const remainingQuota = vr.RemainingQuota as number | undefined;
  if (remainingQuota !== undefined && remainingQuota > 101) return true;

  const emailCredits = vr.EmailCredits as number | undefined;
  const smsCredits = vr.SmsCredits as number | undefined;
  if ((emailCredits !== undefined && emailCredits > 200) || (smsCredits !== undefined && smsCredits > 1)) {
    return true;
  }

  if (hit.services?.includes('SMTP')) return true;

  if (hit.services?.includes('Stripe') && vr.Balance != null) {
    const stripeBalance = parseFloat(String(vr.Balance));
    if (!Number.isNaN(stripeBalance) && stripeBalance > 0) return true;
  }

  if (hit.services?.includes('Twilio')) {
    const twilioBalance = getTwilioBalanceValue(hit);
    if (twilioBalance !== null && twilioBalance > 0) return true;
  }

  return false;
};

export const countSuperHits = (hits: SuperHitCandidate[]): number =>
  hits.filter(isSuperHit).length;
